import { useContext, useEffect } from "react";
import { EditorContext } from "../containers/editor/EditorLayout";
import { customRound, floatToFloatSec, getHMSToSec, playMedia, removeMultiAttrs, setMultiAttrs, stringTocamelCase } from "../_helpers/utils";
import { BRAND, COLLAGE, FRAME, STOCK, UPLOAD_VIDEO, VIDEO } from "../constants/editor";
import { EditorWrapperContext } from "../containers/editor/EditorWrapper";

let intervalId;

const fadeEffectObj = {
  0: ["None", "", null, undefined],
  500: ["0.5 sec"],
  1000: ["1.0 sec", "1 sec"],
  2000: ["2.0 sec", "2 sec"],
};

const fadeIntervalObj = {
  500: [0, 100, 200, 300, 400, 500],
  1000: [0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000],
  2000: [0, 200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000],
};

const animationEngine = {
  meta: {
    speedToDurationMsObj: {
      Fast: 500,
      Medium: 1000,
      Slow: 2000,
    },
  },

  text: {
    meta: {
      entryTypes: ["Flip In X", "Flip In Y", "Bounce In", "Rotate In", "Roll In"],
      exitTypes: ["Flip Out X", "Flip Out Y", "Bounce Out", "Rotate Out", "Roll Out"],
      entryPresets: {
        flipInX: { name: "flipInX", cls: "flip-in-x", duration: 0.8, ease: "ease-out" },
        flipInY: { name: "flipInY", cls: "flip-letter-y", duration: 0.8, ease: "ease-out" },
        bounceIn: { name: "bounceIn", cls: "bounce-in-letter", duration: 0.9 },
        rotateIn: { name: "rotateIn", cls: "rotate-in-letter", duration: 0.8 },
        rollIn: { name: "rollIn", cls: "roll-in-char", duration: 0.8 },
      },
      exitPresets: {
        flipOutX: { name: "flipOutX", cls: "flip-out-char", duration: 0.8, ease: "ease-in" },
        flipOutY: { name: "flipOutY", cls: "flip-letter-y", duration: 0.8, ease: "ease-in" },
        bounceOut: { name: "bounceOut", cls: "bounce-out-letter", duration: 0.9 },
        rotateOut: { name: "rotateOut", cls: "rotate-out-letter", duration: 0.8 },
        rollOut: { name: "rollOut", cls: "roll-out-char", duration: 0.7 },
      },
    },

    beforeWrap: ({ el }) => {
      // ensure perspective wrapper
      if (!el.parentNode.classList.contains("flip-y-root")) {
        const wrapper = document.createElement("div");
        wrapper.className = "flip-y-root";
        el.parentNode.insertBefore(wrapper, el);
        wrapper.appendChild(el);
      }
      // clear old animation spans
      el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());
      return el;
    },

    afterRestore: ({ el }) => {
      const root = el.parentNode;
      if (root && root.classList.contains("flip-y-root")) {
        root.replaceWith(...root.childNodes);
      }
    },

    // wrapTextNodes: ({ node, preset }) => {
    //   const _aEngine = animationEngine.text;
    //   [...node.childNodes].forEach(child => {
    //     if (child.nodeType === 3 && child.textContent.trim().length > 0) {
    //       const fragment = document.createDocumentFragment();
    //       [...child.textContent].forEach(char => {
    //         if (char === "\n") return;
    //         const span = document.createElement("span");
    //         span.className = preset.charClass;
    //         span.innerHTML = char === " " ? "&nbsp;" : char;
    //         fragment.appendChild(span);
    //       });
    //       child.replaceWith(fragment);
    //     } else if (child.nodeType === 1) {
    //       _aEngine.wrapTextNodes({ node: child, preset });
    //     }
    //   });
    // },

    wrapTextNodes: ({ node, preset }) => {
      const _aEngine = animationEngine.text;
      [...node.childNodes].forEach(child => {
        if (child.nodeType === 3 && child.textContent.trim().length > 0) {
          const fragment = document.createDocumentFragment();
          const words = child.textContent.split(" ");

          words.forEach((word, wordIndex) => {
            const wordSpan = document.createElement("span");
            wordSpan.style.display = "inline-block"; // 🔥 IMPORTANT
            wordSpan.style.whiteSpace = "nowrap"; // 🔥 PREVENT BREAK

            [...word].forEach(char => {
              const charSpan = document.createElement("span");
              charSpan.className = preset.charClass;
              charSpan.innerHTML = char === " " ? "&nbsp;" : char;
              wordSpan.appendChild(charSpan);
            });

            fragment.appendChild(wordSpan);

            // add space after word (except last)
            if (wordIndex !== words.length - 1) {
              fragment.appendChild(document.createTextNode(" "));
            }
          });

          child.replaceWith(fragment);
        } else if (child.nodeType === 1) {
          _aEngine.wrapTextNodes({ node: child, preset });
        }
      });
    },

    getAnimationPreset: ({ durationMs, animationClass }) => {
      const { meta, ..._aEngine } = animationEngine.text;
      const animationObj = meta.entryPresets?.[animationClass] || meta.exitPresets?.[animationClass];
      if (!animationObj) return;

      const specialType = ["flipInY", "flipOutY"].includes(animationClass);
      const preset = {
        animationName: animationObj.name,
        charClass: animationObj.cls,
        duration: animationObj.duration,
        ease: animationObj?.ease || "ease-out",
        maxTotalTime: durationMs / 1000,
        beforeWrap: specialType ? _aEngine.beforeWrap : null,
        afterRestore: specialType ? _aEngine.afterRestore : null,
      };
      return preset;
    },

    getPrerequisites: ({ animationClass, durationMs, widgetNode }) => {
      const _aEngine = animationEngine.text;
      return {
        el: widgetNode.querySelector(".primary-text-widget"),
        preset: _aEngine.getAnimationPreset({ durationMs, animationClass }),
        dataset: widgetNode.dataset,
      };
    },

    getSanitizedInnerHtml: ({ el, dataset }) => {
      return !dataset.innerHtml || dataset.innerHtml === "undefined" ? el.innerHTML : dataset.innerHtml;
    },

    start: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.text;
      const { el, preset, dataset } = _aEngine.getPrerequisites({
        animationClass: phase.animationClass,
        durationMs: phase.durationMs,
        widgetNode,
      });
      if (!el || !preset || dataset.animationPhase === phase.phaseId) return;

      // Store data in DOM
      setMultiAttrs(widgetNode, {
        "data-inner-html": _aEngine.getSanitizedInnerHtml({ el, dataset }),
        "data-animation-phase": phase.phaseId,
      });

      // Prepare DOM
      const context = preset?.beforeWrap?.({ el }) || el;
      _aEngine.wrapTextNodes({ node: context, preset });

      const chars = context.querySelectorAll(`.${preset.charClass}`);
      const charCount = chars.length;
      if (!charCount) return;

      // Calculate timing
      const animDuration = Math.min(preset.duration, preset.maxTotalTime);
      const perCharDelay = charCount > 1 ? Math.max((preset.maxTotalTime - animDuration) / (charCount - 1), 0) : 0;

      // Apply animations
      chars.forEach((span, i) => {
        span.classList.add("play-animation");
        span.style.animation = `
        ${preset.animationName} ${animDuration}s ${i * perCharDelay}s both ${preset.ease}
      `;
      });
    },

    continue: ({ phase, timer, widgetNode }) => {
      widgetNode.setAttribute(phase.progressKey, timer - phase.progressOffset);
    },

    stop: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.text;
      const { el, preset, dataset } = _aEngine.getPrerequisites({
        animationClass: phase.animationClass,
        durationMs: phase.durationMs,
        widgetNode,
      });
      if (!el || !preset) return;

      el.innerHTML = _aEngine.getSanitizedInnerHtml({ el, dataset });
      preset?.afterRestore?.({ el });
      removeMultiAttrs(widgetNode, [
        "data-inner-html",
        "data-animation-phase",
        "data-entry-animation-played-duration",
        "data-exit-animation-played-duration",
      ]);
    },

    seek: ({ phase, timer, widgetNode }) => {
      const _aEngine = animationEngine.text;
      const { el, preset, dataset } = _aEngine.getPrerequisites({
        animationClass: phase.animationClass,
        durationMs: phase.durationMs,
        widgetNode,
      });
      if (!el || !preset) return;

      // Prepare DOM once (if condition not meet then it's guranteed that DOM is already prepared)
      if (dataset.animationPhase !== phase.phaseId) {
        _aEngine.start({
          phase,
          widgetNode,
        });
      }

      const chars = el.querySelectorAll(`.${preset.charClass}`);
      const charCount = chars.length;
      if (!charCount) return;

      // Calculate timing
      const animDuration = Math.min(preset.duration, preset.maxTotalTime);
      const perCharDelay = charCount > 1 ? Math.max((preset.maxTotalTime - animDuration) / (charCount - 1), 0) : 0;
      const t = (timer - phase.startTime) / 1000; // current phase time

      // Apply animations
      chars.forEach((span, i) => {
        const off = t - i * perCharDelay; // offset time is current phase time − this character’s start delay
        span.style.animationPlayState = "paused";
        span.style.animationDelay = off <= 0 ? `${-off}s` : off >= animDuration ? `-${animDuration}s` : `-${off}s`;
      });
    },
  },

  widget: {
    getAnimationNode: ({ phase, widgetNode }) => {
      if (phase.engine !== "widget") return;

      // detecting animation node
      let animationNode = widgetNode.querySelector(".animation-node");

      // create animation node if not exists (scenario: playing from beginning)
      if (!animationNode) {
        animationNode = document.createElement("div");
        animationNode.classList.add("animation-node");
        animationNode.style.height = "100%";
        animationNode.style.width = "100%";
        widgetNode.childNodes.forEach(child => animationNode.append(child));
        widgetNode.innerHTML = "";
        widgetNode.append(animationNode);
      }
      return animationNode;
    },

    start: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!widgetNode || !animationNode) return;

      widgetNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
    },

    continue: ({ phase, timer, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!animationNode) return;

      animationNode.setAttribute(phase.progressKey, timer - phase.progressOffset);
    },

    stop: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!widgetNode || !animationNode) return;

      const widgetChildNodes = animationNode.childNodes;
      widgetNode.removeAttribute("data-animation-phase");
      widgetNode.innerHTML = "";
      widgetChildNodes.forEach(n => widgetNode.appendChild(n)); // replacing animation node with original node(s)
    },

    seek: ({ phase, timer, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!widgetNode || !animationNode) return;

      const currentMs = timer - phase.startTime;
      const playedMs = parseInt(animationNode.getAttribute(phase.progressKey) ?? 0);

      // apply animation frame
      widgetNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
      animationNode.style.animationPlayState = "paused";
      animationNode.style.animationDelay =
        currentMs > playedMs ? `-${currentMs - playedMs}ms` : `${playedMs - currentMs}ms`;
    },
  },

  page: {
    regenerateDuplicateIds: ({ backgroundNode }) => {
      backgroundNode.querySelectorAll(".dhp-root-widget").forEach(widget => {
        widget.setAttribute("id", `${widget.id}-copy`);
      });
    },

    resetVideoThumbToEndScreen: ({ backgroundNode }) => {
      backgroundNode.querySelectorAll(".dhp-page-widget").forEach(widget => {
        const { assetType, source, clipped } = widget.dataset;
        if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source)) {
          const { video, trimEnd: trimStopTime } = getVideoNodeAndDataRef({ widgetNode: widget });
          const videoSrc = video?.src?.split("#t=")?.[0];
          video.currentTime = trimStopTime;
          video.src = `${videoSrc}#t=${trimStopTime}`;
        }
        if (assetType === FRAME && clipped === "video") {
          const { video, trimEnd: trimStopTime } = getVideoNodeAndDataRef({ widgetNode: widget });
          const videoSrc = video.querySelector("source")?.src?.split("#t=")?.[0];
          video.currentTime = trimStopTime;
          video.querySelector("source").src = `${videoSrc}#t=${trimStopTime}`;
        }
      });
    },

    getAnimationNode: ({ phase, pageObj, prevPageBlockId }) => {
      if (phase.engine !== "page") return;

      const pageNode = document.getElementById(pageObj.pageId);
      const blockNode = pageNode.querySelector(".dhp-page-block");

      // detecting wrapper/animation node
      let wrapperNode = pageNode.querySelector(".wrapper-node");
      let animationNode = pageNode.querySelector(".animation-node");
      let backgroundNode = pageNode.querySelector(".page-animation-background-node");

      // create wrapper/animation node if not exists (scenario: playing from beginning)
      if (!wrapperNode || !animationNode) {
        animationNode = document.createElement("div");
        animationNode.classList.add("animation-node");

        wrapperNode = document.createElement("div");
        wrapperNode.classList.add("wrapper-node");
        wrapperNode.style.overflow = "hidden";
        wrapperNode.style.height = "100%";
        wrapperNode.style.width = "100%";
        wrapperNode.appendChild(animationNode);

        blockNode.replaceWith(wrapperNode);
        wrapperNode.childNodes[0].appendChild(blockNode);
      }

      // detecting or creating background node to display in the page transition background
      if (prevPageBlockId && !backgroundNode) {
        const _aEngine = animationEngine.page;
        const prevPageBlockNode = document.getElementById(prevPageBlockId);

        backgroundNode = document.createElement("div");
        backgroundNode.classList.add("page-animation-background-node");
        backgroundNode.innerHTML = prevPageBlockNode.innerHTML;
        backgroundNode.style.cssText = prevPageBlockNode.style.cssText;
        backgroundNode.style.position = "absolute";

        // regenerate duplicate ids
        _aEngine.regenerateDuplicateIds({ backgroundNode });

        // reset video thumb to end screen
        _aEngine.resetVideoThumbToEndScreen({ backgroundNode });

        // insert *before* target node
        wrapperNode.parentNode.insertBefore(backgroundNode, wrapperNode);
      }

      return { pageNode, blockNode, wrapperNode, animationNode, backgroundNode };
    },

    start: ({ phase, pageObj, prevPageBlockId }) => {
      const _aEngine = animationEngine.page;
      const { pageNode, animationNode } = _aEngine.getAnimationNode({ phase, pageObj, prevPageBlockId });
      if (!pageNode || !animationNode) return;

      pageNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
    },

    continue: ({ phase, timer, pageObj, prevPageBlockId }) => {
      const _aEngine = animationEngine.page;
      const { animationNode } = _aEngine.getAnimationNode({ phase, pageObj, prevPageBlockId });
      if (!animationNode) return;

      animationNode.setAttribute(phase.progressKey, timer - phase.progressOffset);
    },

    stop: ({ phase, pageObj, prevPageBlockId }) => {
      const _aEngine = animationEngine.page;
      const { pageNode, blockNode, wrapperNode, backgroundNode } = _aEngine.getAnimationNode({
        phase,
        pageObj,
        prevPageBlockId,
      });
      if (!pageNode || !blockNode || !wrapperNode) return;

      pageNode.removeAttribute("data-animation-phase");
      wrapperNode.replaceWith(blockNode); // removing wrapper/animation node (replacing with original node)
      if (backgroundNode) backgroundNode.remove(); // remove page animation background
    },

    seek: ({ phase, timer, pageObj, prevPageBlockId }) => {
      const _aEngine = animationEngine.page;
      const { pageNode, animationNode } = _aEngine.getAnimationNode({ phase, pageObj, prevPageBlockId });
      if (!pageNode || !animationNode) return;

      const currentMs = timer - phase.startTime;
      const playedMs = parseInt(animationNode.getAttribute(phase.progressKey) ?? 0);

      // apply animation frame
      pageNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
      animationNode.style.animationPlayState = "paused";
      animationNode.style.animationDelay =
        currentMs > playedMs ? `-${currentMs - playedMs}ms` : `${playedMs - currentMs}ms`;
    },
  },

  utils: {
    getWidgetAnimData: ({ widgetObj, startTimeMs, stopTimeMs, widgetNode }) => {
      const speedToDurationMsObj = animationEngine.meta.speedToDurationMsObj;
      const { entryTypes: textEntryTypes, exitTypes: textExitTypes } = animationEngine.text.meta;

      const showEntryAnimation = Boolean(widgetObj.data["data-entrance-animation"]);
      const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
      const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);
      const entryAnimationDurationMs = showEntryAnimation && speedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationStopTimeMs = showEntryAnimation && startTimeMs + entryAnimationDurationMs;

      const showExitAnimation = Boolean(widgetObj.data["data-exit-animation"]);
      const exitAnimationSpeed = widgetObj.data["data-exit-animation-speed"];
      const exitAnimationType = widgetObj.data["data-exit-animation-type"];
      const exitAnimationClassName = showExitAnimation && stringTocamelCase(exitAnimationType);
      const exitAnimationDurationMs = showExitAnimation && speedToDurationMsObj[exitAnimationSpeed];
      const exitAnimationStartTimeMs = showExitAnimation && stopTimeMs - exitAnimationDurationMs;

      const isTextEntryType = showEntryAnimation && textEntryTypes.includes(entryAnimationType);
      const isTextExitType = showExitAnimation && textExitTypes.includes(exitAnimationType);
      const isWidgetEntryType = showEntryAnimation && !isTextEntryType;
      const isWidgetExitType = showExitAnimation && !isTextExitType;

      const data = {
        showEntryAnimation,
        entryAnimationType,
        entryAnimationClassName,
        entryAnimationDurationMs,
        entryAnimationStartTimeMs: startTimeMs,
        entryAnimationStopTimeMs,
        showExitAnimation,
        exitAnimationType,
        exitAnimationClassName,
        exitAnimationDurationMs,
        exitAnimationStartTimeMs,
        exitAnimationStopTimeMs: stopTimeMs,
        entryEngine: isTextEntryType ? "text" : isWidgetEntryType ? "widget" : false,
        exitEngine: isTextExitType ? "text" : isWidgetExitType ? "widget" : false,
        animationPhase: widgetNode.getAttribute("data-animation-phase"),
      };

      return {
        ...data,
        animDataObj: data,
      };
    },

    getPageAnimData: ({ pageObj, pageDurationMs }) => {
      const pageNode = document.getElementById(pageObj.pageId);
      const blockNode = pageNode.querySelector(".dhp-page-block");
      const speedToDurationMsObj = animationEngine.meta.speedToDurationMsObj;

      const showEntryAnimation = pageObj.pageTransition.enabled;
      const entryAnimationSpeed = pageObj.pageTransition.speed;
      const entryAnimationType = pageObj.pageTransition.type;
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);
      const entryAnimationDurationMs = showEntryAnimation && speedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationStopTimeMs = showEntryAnimation && pageDurationMs;

      const data = {
        showEntryAnimation,
        entryAnimationType,
        entryAnimationClassName,
        entryAnimationDurationMs,
        entryAnimationStartTimeMs: 0,
        entryAnimationStopTimeMs,
        entryEngine: showEntryAnimation ? "page" : false,
        exitEngine: false,
        animationPhase: pageNode.getAttribute("data-animation-phase"),
      };

      return {
        ...data,
        pageNode,
        blockNode,
        animDataObj: data,
      };
    },

    generateAnimPipelines: ({ animDataObj: data }) => {
      const pipelines = [];

      ["text-entry", "text-exit", "widget-entry", "widget-exit", "page-entry"].forEach(pahse => {
        const [animEngine, animType] = pahse.split("-");
        const isEntryTypeEnabled = animEngine === data.entryEngine;
        const isExitTypeEnabled = animEngine === data.exitEngine;

        pipelines.push({
          phaseId: pahse,
          engine: animEngine,
          type: animType,
          enabled: Boolean(animType === "entry" ? isEntryTypeEnabled : isExitTypeEnabled),
          animationType: animType === "entry" ? data.entryAnimationType : data.exitAnimationType,
          animationClass: animType === "entry" ? data.entryAnimationClassName : data.exitAnimationClassName,
          durationMs: animType === "entry" ? data.entryAnimationDurationMs : data.exitAnimationDurationMs,
          startTime: animType === "entry" ? data.entryAnimationStartTimeMs : data.exitAnimationStartTimeMs,
          stopTime: animType === "entry" ? data.entryAnimationStopTimeMs : data.exitAnimationStopTimeMs,
          progressKey: `data-${animType}-animation-played-duration`,
          progressOffset: animType === "entry" ? 0 : data.exitAnimationStartTimeMs,
        });
      });

      return pipelines;
    },

    dispatchAnimation: ({ phase, timer, widgetNode, pageObj, prevPageBlockId }) => {
      const _aEngine = animationEngine[phase?.engine];
      if (!_aEngine) return;

      if (timer === phase.startTime) {
        _aEngine.start({
          phase,
          widgetNode,
          pageObj,
          prevPageBlockId,
        });
      }

      if (timer > phase.startTime && timer < phase.stopTime) {
        _aEngine.continue({
          phase,
          timer,
          widgetNode,
          pageObj,
          prevPageBlockId,
        });
      }

      if (timer === phase.stopTime) {
        _aEngine.stop({
          phase,
          widgetNode,
          pageObj,
          prevPageBlockId,
        });
      }
    },

    seekAnimation: ({ activePhase, deactivePhase, timer, widgetNode, pageObj, prevPageBlockId }) => {
      // outside playable range → RESTORE deactive animation phase (if any)
      if (deactivePhase) {
        const _aEngine = animationEngine[deactivePhase?.engine];
        if (!_aEngine) return;

        _aEngine.stop({
          phase: deactivePhase,
          widgetNode,
          pageObj,
          prevPageBlockId,
        });
      }

      // within playable range
      if (activePhase) {
        const _aEngine = animationEngine[activePhase?.engine];
        if (!_aEngine) return;

        _aEngine.seek({
          phase: activePhase,
          timer,
          widgetNode,
          pageObj,
          prevPageBlockId,
        });
      }
    },
  },
};

const useDocumentPlayV2 = () => {
  const { audios, widgets, pageNodes, blockNodes, metadata, updateMetadata, isTimeLineViewOpen } =
    useContext(EditorContext);

  const { setPlayingPageIdx, whichPagePlaying, setWhichPagePlaying, documentPlayData, setDocumentPlayData } =
    useContext(EditorWrapperContext);

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{   PAGE PLAY RELATED FUNCTIONS   }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  const getPageData = ({ timer, pageArray, pageIdx }) => {
    const targetPageObj =
      pageIdx >= 0 ? pageArray[pageIdx] : pageArray.find(p => timer >= p.pageStartTimeMs && timer <= p.pageStopTimeMs);

    const prevPageObj = pageArray?.[targetPageObj?.prevPageIdx];
    const nextPageObj = pageArray?.[targetPageObj?.nextPageIdx];

    return {
      pageId: targetPageObj?.pageId,
      pageIdx: targetPageObj?.pageIdx,
      pageObj: pageNodes?.[targetPageObj?.pageIdx],
      pageDurationMs: targetPageObj?.pageDurationMs,
      pageTimer: timer - targetPageObj?.pageStartTimeMs,
      prevPageId: prevPageObj?.pageId,
      prevPageIdx: prevPageObj?.pageIdx,
      prevPageBlockId: prevPageObj?.blockId,
      prevPageBlockIdx: prevPageObj?.blockIdx,
      nextPageId: nextPageObj?.pageId,
      nextPageIdx: nextPageObj?.pageIdx,
      nextPageIdBlockId: nextPageObj?.blockId,
      nextPageIdBlockIdx: nextPageObj?.blockIdx,
    };
  };

  const getWidgetData = ({ widgetObj, pageDurationMs }) => {
    const { startTime = 0, endTime = pageDurationMs } = widgetObj?.duration ?? {};
    const startTimeMs = parseFloat(startTime) * 1000;
    const endTimeMs = parseFloat(endTime) * 1000;
    const stopTimeMs = endTimeMs > pageDurationMs ? pageDurationMs : endTimeMs;
    const widgetNode = document.getElementById(widgetObj.id);
    const assetType = widgetObj.data["data-asset-type"];
    const source = widgetObj.data["data-source"];
    const frameContent = widgetObj.data["data-clipped"];

    return { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent };
  };

  const getVideoNodeAndDataRef = ({ widgetId, widgetNode, videoNode }) => {
    const widget = widgetId ? document.getElementById(widgetId) : widgetNode;
    const assetTypeCollage = widget.dataset.assetType === COLLAGE;
    const video = assetTypeCollage ? videoNode : widget?.querySelector("video");
    const { duration: durationInHMS, volume, trim, loop } = assetTypeCollage ? video?.dataset : widget?.dataset;
    const durationInSec = getHMSToSec({ hms: durationInHMS });
    const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
    const isTrimmed = Boolean(trimArray?.length === 2);
    const trimStart = isTrimmed ? trimArray?.[0] : 0;
    const trimEnd = isTrimmed ? trimArray?.[1] : durationInSec;

    return {
      widget,
      video,
      src: widget.dataset.assetType === FRAME ? video.querySelector("source")?.src : video.src,
      poster:
        widget.dataset.assetType === FRAME ? video.querySelector("source").getAttribute("data-poster") : video.poster,
      volume: volume ? parseInt(volume) : 100,
      durationInSec,
      durationInHMS,
      trimStart,
      trimEnd,
      isMuted: Boolean(video.getAttribute("muted")),
      isLooped: Boolean(loop === "true"),
      isTrimmed: Boolean(trimStart > 0 || trimEnd < durationInSec),
    };
  };

  const resumeDomPlayEnv = ({ timer, pageDurationMs, pageId }) => {
    // setting environment for resuming page play where left off (widget show/hide)
    widgets.forEach(w => {
      if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode } = getWidgetData({ widgetObj: w, pageDurationMs });
        widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "block" : "none";
      }
    });

    // setting environment for resuming page play where left off (page transition + widget animation)
    document.querySelectorAll(".play-animation").forEach(element => {
      element.style.animationPlayState = "";
    });
  };

  const setDomPlayEnvToCustomTime = ({ timer, pageDurationMs, pageObj, prevPageBlockId }) => {
    setPageTransitionCurTime({ timer, pageDurationMs, pageObj, prevPageBlockId });

    widgets.forEach(w => {
      if (w.pageId === pageObj.pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = getWidgetData({
          widgetObj: w,
          pageDurationMs,
        });

        widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "block" : "none";

        if (assetType && startTimeMs < pageDurationMs) {
          setAnimationCurTime({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
        }

        if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source) && startTimeMs < pageDurationMs) {
          setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (assetType === COLLAGE && startTimeMs < pageDurationMs) {
          widgetNode.querySelectorAll("video").forEach(videoNode => {
            setVideoCurTime({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
          });
        }

        if (assetType === FRAME && frameContent === "video" && startTimeMs < pageDurationMs) {
          setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
        }
      }
    });
  };

  const stopDomPlay = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.querySelectorAll(".play-animation").forEach(animationNode => {
      animationNode.style.animationPlayState = "paused";
    });
    document.querySelectorAll(`video[data-playing="true"], audio[data-playing="true"]`).forEach(mediaNode => {
      mediaNode.pause();
      mediaNode.removeAttribute("data-playing");
    });
  };

  const setVideoCurTime = ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
    const {
      video,
      trimStart: trimStartTime,
      trimEnd: trimStopTime,
    } = getVideoNodeAndDataRef({ widgetNode, videoNode });

    if (timer > startTimeMs && timer < stopTimeMs) {
      // when widget is within playable time range
      const widgetPlayedDurationMs = timer - startTimeMs;
      const videoTrimmedDurationMs = (trimStopTime - trimStartTime) * 1000;
      const videoCurTimeWithinPlayableRange =
        (videoTrimmedDurationMs > widgetPlayedDurationMs
          ? widgetPlayedDurationMs
          : widgetPlayedDurationMs % videoTrimmedDurationMs) / 1000;

      video.currentTime = trimStartTime + videoCurTimeWithinPlayableRange;
    } else {
      video.currentTime = trimStartTime;
    }
  };

  const videoPlay = ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
    const {
      video,
      volume,
      trimStart: trimStartTime,
      trimEnd: trimStopTime,
      isMuted,
      isLooped,
    } = getVideoNodeAndDataRef({ widgetNode, videoNode });

    if (video) {
      if (timer === startTimeMs) {
        if (isMuted) video.muted = false;
        video.currentTime = trimStartTime;
        video.volume = volume / 100;
        playMedia(video);
        video.setAttribute("data-playing", "true");
      }

      if (timer > startTimeMs && timer < stopTimeMs) {
        const videoCurTime = floatToFloatSec(video.currentTime);
        if (video.getAttribute("data-playing") !== "true") video.setAttribute("data-playing", "true");
        if (!(videoCurTime >= trimStartTime && videoCurTime <= trimStopTime)) {
          // when video is outside playable time range
          video.currentTime = trimStartTime;
          if (isLooped) {
            if (video.paused) playMedia(video);
          } else {
            video.pause();
          }
        } else {
          if (video.paused) playMedia(video);
        }
      }

      if (timer === stopTimeMs) {
        video.pause();
        if (isMuted) video.muted = true;
        video.removeAttribute("data-playing");
        video.currentTime = trimStartTime;
      }
    }
  };

  const setAnimationCurTime = ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
    let activePhase, deactivePhase;
    const _aEngine = animationEngine.utils;
    const {
      animDataObj,
      showEntryAnimation,
      showExitAnimation,
      animationPhase: lastActivePhase,
    } = _aEngine.getWidgetAnimData({
      widgetObj,
      startTimeMs,
      stopTimeMs,
      widgetNode,
    });
    if (!showEntryAnimation && !showExitAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }

    for (const phase of animationPipelines) {
      if (phase.enabled && phase.phaseId !== activePhase && phase.phaseId === lastActivePhase) {
        deactivePhase = phase;
        break;
      }
    }

    _aEngine.seekAnimation({
      activePhase,
      deactivePhase,
      timer,
      widgetNode,
    });
  };

  const animationPlay = ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
    let activePhase;
    const _aEngine = animationEngine.utils;
    const { animDataObj, showEntryAnimation, showExitAnimation } = _aEngine.getWidgetAnimData({
      widgetObj,
      startTimeMs,
      stopTimeMs,
      widgetNode,
    });
    if (!showEntryAnimation && !showExitAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }
    if (!activePhase) return;

    _aEngine.dispatchAnimation({
      phase: activePhase,
      timer,
      widgetNode,
    });
  };

  const widgetPlay = ({ timer, pageDurationMs, pageObj, action }) => {
    // widget play-pause
    widgets.forEach(w => {
      if (w.pageId === pageObj.pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = getWidgetData({
          widgetObj: w,
          pageDurationMs,
        });

        if (!widgetNode) return;

        if (timer === startTimeMs) {
          widgetNode.style.display = "block";
        }

        if (assetType && startTimeMs < pageDurationMs) {
          if (action === "force_stop") {
            setAnimationCurTime({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
          }
          animationPlay({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
        }

        if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source) && startTimeMs < pageDurationMs) {
          videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (assetType === COLLAGE && startTimeMs < pageDurationMs) {
          widgetNode.querySelectorAll("video").forEach(videoNode => {
            videoPlay({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
          });
        }

        if (assetType === FRAME && frameContent === "video" && startTimeMs < pageDurationMs) {
          videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (timer === stopTimeMs && stopTimeMs < pageDurationMs) {
          widgetNode.style.display = "none";
        }

        if (timer === pageDurationMs) {
          // page duration end
          widgetNode.style.display = "block";
        }
      }
    });
  };

  const setPageTransitionCurTime = ({ pageObj, pageDurationMs, timer, prevPageBlockId }) => {
    let activePhase;
    const _aEngine = animationEngine.utils;
    const { pageNode, blockNode, animDataObj, showEntryAnimation } = _aEngine.getPageAnimData({
      pageObj,
      pageDurationMs
    });

    // reset playing page props
    pageNode.classList.add("page-playing");
    blockNode.style.pointerEvents = "none";
    blockNode.classList.add("canvas-disabled");
    localStorage.setItem("widget.event.isActive", "true");
    setPlayingPageIdx(pageObj.pageIdx);

    if (!showEntryAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }

    _aEngine.seekAnimation({
      activePhase,
      deactivePhase: null,
      timer,
      pageObj,
      prevPageBlockId,
    });
  }

  const pageTransitionPlay = ({ pageObj, pageDurationMs, timer, prevPageBlockId }) => {
    let activePhase;
    const _aEngine = animationEngine.utils;
    const { animDataObj, showEntryAnimation } = _aEngine.getPageAnimData({
      pageObj,
      pageDurationMs
    });
    if (!showEntryAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }
    if (!activePhase) return;

    _aEngine.dispatchAnimation({
      phase: activePhase,
      timer,
      pageObj,
      prevPageBlockId,
    });
  };

  const pagePlay = ({ timer, pageDurationMs, pageObj, prevPageBlockId }) => {
    const pageNode = document.getElementById(pageObj.pageId);
    const blockNode = pageNode.querySelector(".dhp-page-block");

    if (timer === 0) {
      // reset environments ready for page play
      updateCanvasControllerPageNo({ pageNo: pageObj.pageIdx + 1 });
      hideInactivePages({ activePageId: pageObj.pageId });
      setPlayingPageIdx(pageObj.pageIdx);

      // page play start
      pageNode.classList.add("page-playing");
      blockNode.style.pointerEvents = "none";
      blockNode.classList.add("canvas-disabled");
      pageTransitionPlay({ timer, pageDurationMs, pageObj, prevPageBlockId });
      localStorage.setItem("widget.event.isActive", "true");
    }

    if (timer > 0 && timer < pageDurationMs) {
      // page play continues
      pageTransitionPlay({ timer, pageDurationMs, pageObj, prevPageBlockId });
    }

    if (timer === pageDurationMs) {
      // page play stop
      pageNode.classList.remove("page-playing");
      blockNode.style.pointerEvents = "";
      blockNode.classList.remove("canvas-disabled");
      pageTransitionPlay({ timer, pageDurationMs, pageObj, prevPageBlockId });
      localStorage.removeItem("widget.event.isActive");
    }
  };

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{ DOCUMENT PLAY RELATED FUNCTIONS }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  const getDocumentData = () => {
    let pageArray = [];
    let documentDurationMs = 0;

    pageNodes.forEach((pageObj, pageIdx) => {
      const blockObj = blockNodes[pageIdx];
      const pageDurationMs = parseFloat(pageObj.pageDuration) * 1000;
      const pageStartTimeMs = documentDurationMs;
      const pageStopTimeMs = pageStartTimeMs + pageDurationMs;
      const prevPageIdx = pageNodes?.[pageIdx - 1]?.pageIdx;
      const nextPageIdx = pageNodes?.[pageIdx + 1]?.pageIdx;

      documentDurationMs += pageDurationMs;
      pageArray = [
        ...pageArray,
        {
          pageId: pageObj.pageId,
          pageIdx: pageIdx,
          blockId: blockObj.blockId,
          blockIdx: pageIdx,
          pageDurationMs,
          pageStartTimeMs,
          pageStopTimeMs,
          prevPageIdx: prevPageIdx ?? null,
          nextPageIdx: nextPageIdx ?? null,
        },
      ];
    });

    return {
      documentDurationMs,
      firstPageObj: pageArray[0],
      pageArray,
      scrollData: getScrollData(),
    };
  };

  const getScrollData = () => {
    const scrollContainer = document.getElementById("timeline-body");
    const count = Math.ceil(scrollContainer.scrollWidth / scrollContainer.offsetWidth);
    const breakPoints = [...Array(count)].map((_, i) => scrollContainer.offsetWidth * i);
    const rangePoints = breakPoints.map((p, i) => {
      const nextPoint = breakPoints[i + 1];
      return {
        min: p,
        max: nextPoint ? nextPoint - 1 : scrollContainer.scrollWidth,
      };
    });

    return { rangePoints };
  };

  const updateCanvasControllerPageNo = ({ pageNo }) => {
    const displayNode = document.querySelector(".canvas-controller .current-page");
    if (displayNode) displayNode.innerText = pageNo;
  };

  const hideInactivePages = ({ activePageId }) => {
    document.querySelectorAll(".dhp-page-canvas").forEach(page => {
      page.style.display = page.id === activePageId ? "block" : "none";
    });
  };

  const disableAssetPane = () => {
    const assetWrapper = document.querySelector(".dhp-secondAside .assets-wrapper");
    if (assetWrapper) assetWrapper.style.display = "none";
    // handleWidgetAction("*", false, false, 0, "returnToWidgetList");
  };

  const resetTimelineScrollPosition = () => {
    const scrollContainer = document.getElementById("timeline-body");
    const playHead = document.querySelector(".document-timeline-playhead");

    if (!scrollContainer) return;
    if (playHead) {
      // play initiated after pause
      const left = parseInt(playHead.style.left);
      scrollContainer.scrollLeft = left;
      scrollContainer.dataset.scrollLeft = left;
    } else {
      // new play
      scrollContainer.scroll(0, 0);
      scrollContainer.dataset.scrollLeft = 0;
    }
  };

  // send target page to a spacic playing state in pause mode
  const setTargetPagePlayToCustomTime = ({ documentTimer, documentProgress, targetPageObj }) => {
    setDocumentPlayData({
      ...documentPlayData,
      action: "pause",
      pageId: targetPageObj.pageId,
      pageIdx: targetPageObj.pageIdx,
      documentTimer: documentTimer - (documentTimer % 100), // documentTimer may come as 550ms, so making it divisible by 100 and remainder should be 0  (result => 550ms becomes 500ms)
      documentProgress,
    });
    setWhichPagePlaying({
      action: "pause",
      playingPageIdx: targetPageObj.pageIdx,
      triggerPoint: "slider",
    });
  };

  // update document-play as per playhead new position
  const documentPlayHeadHandler = ({ transX, scaleWidth }) => {
    const { documentDurationMs, pageArray } = documentPlayData;
    const documentDuration = documentDurationMs / 1000;
    const trackWidth = documentDuration * scaleWidth;
    const documentProgress = Math.round((transX * 100) / trackWidth);

    if (documentProgress >= 1 && documentProgress <= 99) {
      const documentTimer = (documentProgress * documentDurationMs) / 100;
      const targetPageObj = pageArray.find(
        p => documentTimer >= p.pageStartTimeMs && documentTimer <= p.pageStopTimeMs
      );

      // if playhead new position is outside active-page/playing-page
      if (targetPageObj.pageIdx !== documentPlayData.pageIdx) {
        // reset environments ready for target page to play
        updateCanvasControllerPageNo({ pageNo: targetPageObj.pageIdx + 1 });
        hideInactivePages({ activePageId: targetPageObj.pageId });
        setPlayingPageIdx(targetPageObj.pageIdx);
      }

      // finally reset environments ready to play from new position
      setTargetPagePlayToCustomTime({ documentTimer, documentProgress, targetPageObj });
    } else {
      // exit play mode if progress is 0 or 100
      setWhichPagePlaying({
        action: "force_stop",
        playingPageIdx: documentPlayData.pageIdx,
      });
    }
  };

  const documentPlay = ({ timer, documentDurationMs, pageObj, pageArray, scrollData }) => {
    const actionObj = timer === documentDurationMs ? { action: "natural_stop" } : {};
    const progress = customRound((100 / documentDurationMs) * timer, 2);

    setDocumentPlayData({
      ...documentPlayData,
      ...actionObj,
      pageId: pageObj.pageId,
      pageIdx: pageObj.pageIdx,
      documentTimer: timer,
      documentProgress: progress,
      documentDurationMs,
      pageArray,
      scrollData,
    });
  };

  const stopDocumentPlay = ({ delay = 0, action } = {}) => {
    const { firstPageObj } = getDocumentData();
    const scrollContainer = document.getElementById("timeline-body");
    setTimeout(() => {
      if (scrollContainer) {
        scrollContainer.scrollLeft = 0;
        scrollContainer.dataset.scrollLeft = 0;
      }
      updateCanvasControllerPageNo({ pageNo: firstPageObj.pageIdx + 1 });
      hideInactivePages({ activePageId: firstPageObj.pageId });
      setDocumentPlayData({
        action: null,
        pageId: null,
        pageIdx: null,
        documentTimer: null,
        documentProgress: 0,
        documentDurationMs: 0,
        pageArray: [],
        scrollData: null,
      });
      setWhichPagePlaying(action === "natural_stop" ? { ...whichPagePlaying, action } : {});
      setPlayingPageIdx(null);
    }, delay);
  };

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{   AUDIO PLAY RELATED FUNCTIONS  }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  const getAllAudioData = () => {
    const allAudioData = {};
    audios?.forEach(audioObj => {
      const audioData = getAudioData(audioObj);
      allAudioData[audioObj.id] = audioData;
    });

    return { allAudioData };
  };

  const getAudioData = audioObj => {
    let fadeData = {};
    const audio = document.getElementById(`player-${audioObj.id}`);
    const durationInHMS = audioObj.data["data-duration"];
    const durationInSec = getHMSToSec({ hms: durationInHMS });

    const fadeInValue = audioObj.data["data-fade-in-value"];
    const fadeOutValue = audioObj.data["data-fade-out-value"];
    const fadeInMs = parseInt(Object.keys(fadeEffectObj).find(k => fadeEffectObj[k].includes(fadeInValue)));
    const fadeOutMs = parseInt(Object.keys(fadeEffectObj).find(k => fadeEffectObj[k].includes(fadeOutValue)));

    const trim = audioObj.data["data-trim"];
    const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
    const isTrimmed = Boolean(trimArray?.length === 2);
    const trimStart = isTrimmed ? trimArray?.[0] : 0;
    const trimStop = isTrimmed ? trimArray?.[1] : durationInSec;

    const volume = parseInt(audioObj.data["data-volume"] ?? 100);
    const startTimeMs = parseFloat(audioObj.startTime) * 1000;
    const stopTimeMs = parseFloat(audioObj.endTime) * 1000;
    const trimStartTimeMs = trimStart * 1000;
    const trimStopTimeMs = trimStop * 1000;
    const trimmedDurationMs = trimStopTimeMs - trimStartTimeMs;

    const fadeInStartTimeMs = fadeInMs > 0 && startTimeMs;
    const fadeInStopTimeMs = fadeInMs > 0 && startTimeMs + fadeInMs;
    const fadeOutStartTimeMs = fadeOutMs > 0 && stopTimeMs - fadeOutMs;
    const fadeOutStopTimeMs = fadeOutMs > 0 && stopTimeMs;

    if (fadeInMs > 0 || fadeOutMs > 0) {
      fadeData = getFadeData({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs });
    }

    return {
      audio,
      volume,
      startTimeMs,
      stopTimeMs,
      trimStartTimeMs,
      trimStopTimeMs,
      trimmedDurationMs,
      fadeInStartTimeMs,
      fadeInStopTimeMs,
      fadeOutStartTimeMs,
      fadeOutStopTimeMs,
      ...fadeData,
      isMuted: Boolean(volume === 0),
      isLooped: Boolean(stopTimeMs > trimStopTimeMs),
      isTrimmed: Boolean(trimStartTimeMs > 0 || trimStopTimeMs < stopTimeMs),
      isFadeIn: Boolean(fadeInMs > 0),
      isFadeOut: Boolean(fadeOutMs > 0),
    };
  };

  const getFadeData = ({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs }) => {
    let fadeInIntervals = [];
    let fadeInVolumes = [];
    let fadeOutIntervals = [];
    let fadeOutVolumes = [];

    if (fadeInMs > 0) {
      const intervals = fadeIntervalObj[fadeInMs];
      const volumeFactor = volume / (intervals.length - 1);

      for (let i = 0; i < intervals.length; i++) {
        fadeInIntervals = [...fadeInIntervals, fadeInStartTimeMs + intervals[i]];

        if (i === 0) fadeInVolumes = [...fadeInVolumes, 0];
        if (i > 0 && i < intervals.length - 1) fadeInVolumes = [...fadeInVolumes, parseInt(i * volumeFactor)];
        if (i === intervals.length - 1) fadeInVolumes = [...fadeInVolumes, volume];
      }
    }

    if (fadeOutMs > 0) {
      const fadeoutIntervals = JSON.parse(JSON.stringify(fadeIntervalObj[fadeOutMs]));
      const intervals = fadeoutIntervals.reverse();
      const volumeFactor = volume / (intervals.length - 1);

      for (let i = intervals.length - 1; i >= 0; i--) {
        fadeOutIntervals = [...fadeOutIntervals, fadeOutStartTimeMs + intervals[i]];

        if (i === intervals.length - 1) fadeOutVolumes = [...fadeOutVolumes, volume];
        if (i > 0 && i < intervals.length - 1) fadeOutVolumes = [...fadeOutVolumes, parseInt(i * volumeFactor)];
        if (i === 0) fadeOutVolumes = [...fadeOutVolumes, 0];
      }
    }

    return { fadeInIntervals, fadeInVolumes, fadeOutIntervals, fadeOutVolumes };
  };

  const setAudioCurTime = ({ timer, allAudioData }) => {
    audios?.forEach(audioObj => {
      const { audio, trimStartTimeMs, trimStopTimeMs, startTimeMs, stopTimeMs } = allAudioData[audioObj.id];
      if (audio) {
        const trimStartTime = trimStartTimeMs / 1000;
        const trimStopTime = trimStopTimeMs / 1000;

        if (timer > startTimeMs && timer < stopTimeMs) {
          // when audio is within playable time range
          const audioPlayedDurationMs = timer - startTimeMs;
          const audioTrimmedDurationMs = (trimStopTime - trimStartTime) * 1000;
          const audioCurTimeWithinPlayableRange =
            (audioTrimmedDurationMs > audioPlayedDurationMs
              ? audioPlayedDurationMs
              : audioPlayedDurationMs % audioTrimmedDurationMs) / 1000;

          audio.currentTime = trimStartTime + audioCurTimeWithinPlayableRange;
        } else {
          audio.currentTime = trimStartTime;
        }
      }
    });
  };

  const audioPlay = ({ timer, documentDurationMs, allAudioData }) => {
    audios?.forEach(audioObj => {
      const {
        audio,
        volume,
        startTimeMs,
        stopTimeMs,
        trimStartTimeMs,
        trimStopTimeMs,
        fadeInIntervals,
        fadeInVolumes,
        fadeOutIntervals,
        fadeOutVolumes,
        isMuted,
        isLooped,
        isFadeIn,
        isFadeOut,
      } = allAudioData[audioObj.id];

      if (audio) {
        if (timer === startTimeMs) {
          if (isMuted) audio.muted = false;
          if (!isFadeIn) audio.volume = volume / 100;
          audio.currentTime = trimStartTimeMs / 1000;
          playMedia(audio);
          audio.setAttribute("data-playing", "true");
        }

        // fadeIn audio effect
        if (isFadeIn && fadeInIntervals.includes(timer)) {
          const idx = fadeInIntervals.indexOf(timer);
          audio.volume = fadeInVolumes[idx] / 100;
        }

        // fadeOut audio effect
        if (isFadeOut && fadeOutIntervals.includes(timer)) {
          const idx = fadeOutIntervals.indexOf(timer);
          audio.volume = fadeOutVolumes[idx] / 100;
        }

        if (timer > startTimeMs && timer < stopTimeMs) {
          const audioCurTimeMs = floatToFloatSec(audio.currentTime) * 1000;
          if (audio.getAttribute("data-playing") !== "true") audio.setAttribute("data-playing", "true");
          if (!(audioCurTimeMs >= trimStartTimeMs && audioCurTimeMs <= trimStopTimeMs)) {
            // when audio is outside playable time range
            audio.currentTime = trimStartTimeMs / 1000;
            if (isLooped) {
              if (audio.paused) playMedia(audio);
            } else {
              audio.pause();
            }
          } else {
            if (audio.paused) playMedia(audio);
          }
        }

        if ([stopTimeMs, documentDurationMs].includes(timer)) {
          audio.pause();
          if (isMuted) audio.muted = true;
          audio.removeAttribute("data-playing");
          audio.currentTime = trimStartTimeMs / 1000;
        }
      }
    });
  };

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{          ALL USEEFFECTS         }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  useEffect(() => {
    const action = documentPlayData.action;

    if (action === "play" && !intervalId) {
      let timer = documentPlayData?.documentTimer ?? 0;
      const { documentDurationMs, firstPageObj, pageArray, scrollData } = getDocumentData();
      const { pageId, pageIdx, pageObj, pageDurationMs, pageTimer, prevPageBlockId } = getPageData({
        timer,
        pageArray,
      });
      const { allAudioData } = getAllAudioData();

      if (!pageId || pageIdx < 0 || !pageObj) return;

      resetTimelineScrollPosition(); // reset scroll position to "default" OR "resume point"
      resumeDomPlayEnv({ timer: pageTimer, pageDurationMs, pageId }); // reset widget timelines and animations to "default" OR "resume point"

      if (timer === 0) {
        // reset environments ready for document play
        disableAssetPane();
        updateMetadata({
          ...metadata,
          activePageId: firstPageObj.pageId,
          activePageIdx: firstPageObj.pageIdx,
          activeBlockId: firstPageObj.blockId,
          activeBlockIdx: firstPageObj.blockIdx,
          activeWidgetId: false,
          activeWidgetType: false,
          activeOutSideCanvasArea: true, // show page dimension and hide page background
        });

        // document/page play start
        documentPlay({ timer: 0, documentDurationMs, pageObj, pageArray, scrollData });
        pagePlay({ timer: 0, pageDurationMs, pageObj, prevPageBlockId });
        widgetPlay({ timer: 0, pageDurationMs, pageObj });
        audioPlay({ timer: 0, allAudioData });
      }

      intervalId = setInterval(() => {
        // pageTimer is relative time for page which is derived from documentTimer (i.e. timer)
        timer += 100;
        const { pageId, pageIdx, pageObj, pageDurationMs, pageTimer, nextPageIdx, prevPageBlockId } = getPageData({
          timer,
          pageArray,
        });

        if (!pageId || pageIdx < 0 || !pageObj) return;

        // document/page play continues
        widgetPlay({ timer: pageTimer, pageDurationMs, pageObj });
        pagePlay({ timer: pageTimer, pageDurationMs, pageObj, prevPageBlockId });
        documentPlay({ timer, documentDurationMs, pageObj, pageArray, scrollData });
        audioPlay({ timer, allAudioData });

        // start next page play at the transition point i.e. previous page's stop time (initial 0-100ms of next page play starts from here then continues from above block)
        if (pageTimer === pageDurationMs && nextPageIdx) {
          const { pageId, pageObj, pageDurationMs, prevPageBlockId } = getPageData({ pageArray, pageIdx: nextPageIdx });
          resumeDomPlayEnv({ timer: 0, pageDurationMs, pageId }); // important! because for this page all widget timelines (mainly) and animations need to be reset to default (timer = 0)
          pagePlay({ timer: 0, pageDurationMs, pageObj, prevPageBlockId });
          widgetPlay({ timer: 0, pageDurationMs, pageObj });
        }
      }, 100);
    }

    if (action === "pause") {
      stopDomPlay();
    }

    if (action === "natural_stop") {
      stopDomPlay();
      stopDocumentPlay({ action });
    }

    if (action === "force_stop") {
      let timer = documentPlayData?.documentTimer ?? 0;
      const { documentDurationMs, pageArray } = getDocumentData();
      const { pageId, pageIdx, pageObj, pageDurationMs, prevPageBlockId } = getPageData({ timer, pageArray });
      const { allAudioData } = getAllAudioData();

      if (!pageId || pageIdx < 0 || !pageObj) return;

      widgetPlay({ timer: pageDurationMs, pageDurationMs, pageObj, action });
      pagePlay({ timer: pageDurationMs, pageDurationMs, pageObj, prevPageBlockId });
      audioPlay({ timer: documentDurationMs, documentDurationMs, allAudioData });
      stopDomPlay();
      stopDocumentPlay({ action });
    }

    return () => {
      stopDomPlay();
    };
  }, [documentPlayData.action]);

  useEffect(() => {
    if (
      whichPagePlaying.action === "pause" &&
      whichPagePlaying.triggerPoint === "slider" &&
      isTimeLineViewOpen === "document-timeline"
    ) {
      const { pageArray } = getDocumentData();
      const { allAudioData } = getAllAudioData();

      // determine last playingPageIdx
      const playingPageIdx = document.querySelector(".dhp-page-canvas.page-playing")?.getAttribute("data-page-idx");

      // if playhead new position is outside active-page/playing-page
      if (playingPageIdx !== documentPlayData.pageIdx) {
        // reset last page's environments ready to default (as if last page has completed playing 100% and set to default screen of that page)
        const { pageId, pageIdx, pageObj, pageDurationMs, prevPageBlockId } = getPageData({
          pageArray,
          pageIdx: playingPageIdx,
        });
        if (!pageId || pageIdx < 0 || !pageObj) return;

        widgetPlay({ timer: pageDurationMs, pageDurationMs, pageObj });
        pagePlay({ timer: pageDurationMs, pageDurationMs, pageObj, prevPageBlockId });
      }

      // setting environment ready to resume page play from new position
      const timer = documentPlayData?.documentTimer ?? 0;
      const { pageId, pageIdx, pageObj, pageDurationMs, pageTimer, prevPageBlockId } = getPageData({
        timer,
        pageArray,
      });
      if (!pageId || pageIdx < 0 || !pageObj) return;

      setDomPlayEnvToCustomTime({ timer: pageTimer, pageDurationMs, pageObj, prevPageBlockId });
      setAudioCurTime({ timer, allAudioData });
    }
  }, [whichPagePlaying]);

  return { documentPlayHeadHandler };
};

export default useDocumentPlayV2;
